import { useEffect, useState } from "react";
import classNames from "classnames";
import { useGlobalReducer } from "../../../GlobalContext";
import { GetEntityRecordById } from "../../api/entityService";
import { formatNumber } from "../../utils/utility";
import {
  convertUTCDateTimeToLocal,
  toDisplayDateTime,
} from "../../blocks/fields/formatDate";
import { RecordLink } from "../../blocks/layout/RecordLink";
import { Loading } from "../../elements/Loading";
import { usePanelPageTitles } from "../PanelPageContext";
import { DoneStage, ImportStatus, useImportStatus } from "./useImportStatus";

export interface DataImportStatusProps {
  importId: string;
}

function ImportResult({ status }: { status: ImportStatus }) {
  const listId = status.NewListId ?? status.ListId;

  return (
    <div className="alert alert-info mb-0" role="status">
      Import complete. Static Marketing List <strong>{status.ListName}</strong>{" "}
      {status.NewListId ? "created with" : "updated with"}{" "}
      <strong>{formatNumber(status.ListMemberCount)}</strong>{" "}
      {status.NewListId ? "members." : "additional members."}{" "}
      <RecordLink
        to="/static-marketing-list"
        id={listId}
        text="Open list"
        newTab="button"
      />
    </div>
  );
}

function CountTile({
  label,
  count,
  duplicates,
}: {
  label: string;
  count?: number;
  duplicates?: number;
}) {
  return (
    <div className="col-6 col-md-3">
      <div className="border rounded p-3 h-100">
        <div className="fs-7 text-muted">{label}</div>
        <div className="h3 fw-semibold mb-0">{formatNumber(count ?? 0)}</div>
        {Boolean(duplicates) && (
          <div className="fs-7 text-muted">
            {formatNumber(duplicates)}{" "}
            {duplicates === 1 ? "duplicate" : "duplicates"}
          </div>
        )}
      </div>
    </div>
  );
}

interface ImportRecordFields {
  source?: string;
  createdon?: string;
}

// The status has no date, so the record is read once for when the import was made.
function useImportTitle(importId: string, source?: string) {
  const globalDispatch = useGlobalReducer();
  const [fields, setFields] = useState<ImportRecordFields>();

  useEffect(() => {
    let cancelled = false;

    GetEntityRecordById(globalDispatch, "dataimport", importId).then(
      (response: { data?: { Fields?: ImportRecordFields } }) => {
        if (!cancelled) {
          setFields(response?.data?.Fields ?? {});
        }
      }
    );

    return () => {
      cancelled = true;
    };
  }, [globalDispatch, importId]);

  const createdOn = toDisplayDateTime(
    convertUTCDateTimeToLocal(fields?.createdon)?.toDate()
  );
  const parts = [source || fields?.source, createdOn].filter(Boolean);

  return fields && parts.length > 0 ? parts.join(" - ") : undefined;
}

/** An import's progress while it runs, and what it did once it's done. */
export function DataImportStatus({ importId }: DataImportStatusProps) {
  const { status, paused } = useImportStatus(importId);
  const title = useImportTitle(importId, status?.Source || undefined);

  usePanelPageTitles({ page: title, panel: "" });

  if (status === undefined) {
    return <Loading />;
  }

  if (status === null) {
    return (
      <div className="alert alert-danger mb-0" role="status">
        The status of this import could not be read.
      </div>
    );
  }

  const isDone = status.Stage === DoneStage;
  const isSuccess = isDone && Boolean(status.ListId || status.NewListId);
  const errors = Object.entries(status.Errors ?? {});

  return (
    <div className="d-flex flex-column gap-3">
      {!isSuccess && (
        <div
          className={classNames("alert mb-0", isDone ? "alert-danger" : "alert-info")}
          role="status"
        >
          {status.Message}
        </div>
      )}
      {paused && (
        <p className="mb-0">
          Automatic status checks have paused. Refresh the page to resume.
        </p>
      )}
      {isSuccess && (
        <>
          <div className="row g-3">
            <CountTile label="Processed" count={status.ProcessedCount} />
            <CountTile
              label="New Contacts"
              count={status.NewContactCount}
              duplicates={status.NewDuplicateCount}
            />
            <CountTile
              label="Existing Contacts"
              count={status.ExistingContactCount}
              duplicates={status.ExistingDuplicateCount}
            />
            <CountTile label="Errors" count={status.ErrorCount} />
          </div>
          <ImportResult status={status} />
        </>
      )}
      {isSuccess && errors.length > 0 && (
        <div>
          <h3 className="h6 fw-semibold mb-2">Errors ({formatNumber(errors.length)})</h3>
          <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
            {errors.map(([reference, message]) => (
              <li key={reference} className="d-flex gap-3">
                <span className="fw-semibold flex-shrink-0">{reference}</span>
                <span>{message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default DataImportStatus;
